import dayjs from 'dayjs';

import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import { createQueryParams, INDICES, type QueryParams } from '@/shared/api/openmetadata';
import type { OpenMetadataHit, OpenMetadataResponse, OpenMetadataTag } from '@/shared/api/openmetadata';

import type { MyAsset } from '@/entities/asset';

export type MyAssetsResponse = {
  items: MyAsset[];
  totalCount: number;
  nextPage?: number;
};

/**
 * OpenMetadata Hit을 MyAsset으로 변환합니다
 * @param hit - OpenMetadata 검색 결과 아이템
 * @returns MyAsset 객체
 */
const mapHitToMyAsset = (hit: OpenMetadataHit): MyAsset => {
  const source = hit._source;
  const primaryOwner = source.owners && source.owners.length > 0 ? source.owners[0] : null;

  return {
    id: hit._id,
    fqn: source.fullyQualifiedName || '',
    name: source.displayName || source.name || 'Unknown',
    type: source.entityType || 'table',
    description: source.description || '',
    owner: primaryOwner?.displayName || primaryOwner?.name || 'Unknown',
    service: source.service?.displayName || source.service?.name || 'Unknown',
    database: source.database?.displayName || source.database?.name || '',
    schema: source.databaseSchema?.displayName || source.databaseSchema?.name || '',
    tags: source.tags?.map((tag: OpenMetadataTag) => tag.tagFQN || tag.name) || [],
    updatedAt: source.updatedAt && dayjs(source.updatedAt).isValid() ? dayjs(source.updatedAt).toISOString() : null,
  };
};

/**
 * 엔티티 타입 필터를 생성합니다
 * @param entityType - 필터링할 엔티티 타입
 * @returns 엔티티 타입 필터 객체
 */
const createEntityTypeFilter = (entityType: string) => ({
  query: {
    bool: {
      must: [
        {
          bool: {
            should: [
              {
                term: { entityType },
              },
            ],
          },
        },
      ],
    },
  },
});

/**
 * OpenMetadata API에 쿼리를 요청합니다 (공통 API 호출 함수)
 * @param queryParams - 검색 쿼리 파라미터
 * @returns OpenMetadata API 응답 데이터
 */
const queryOpenMetadataApi = async (queryParams: QueryParams): Promise<OpenMetadataResponse> => {
  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params: queryParams,
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });

  return response.data;
};

/**
 * OpenMetadata API에서 자산 목록을 조회합니다
 * @param queryParams - 검색 쿼리 파라미터
 * @returns 변환된 최근 변경된 자산 목록
 */
const fetchAssetsFromApi = async (queryParams: QueryParams): Promise<MyAsset[]> => {
  const data = await queryOpenMetadataApi(queryParams);
  const hits = data.hits?.hits || [];
  return hits.map(mapHitToMyAsset);
};

/**
 * 최근 변경된 자산을 조회합니다 (페이지네이션 지원)
 * @param from - 시작 인덱스
 * @param size - 페이지 크기
 * @returns 최근 변경된 자산 목록과 페이지 정보
 */
export const fetchMyAssets = async (from: number = 0, size: number = 15): Promise<MyAssetsResponse> => {
  const params = createQueryParams({
    index: INDICES.DATA_ASSET,
    from,
    size,
    sort_field: 'updatedAt',
    sort_order: 'desc',
    exclude_source_fields: 'columns',
  });

  const data = await queryOpenMetadataApi(params);
  const hits = data.hits?.hits || [];
  const items = hits.map(mapHitToMyAsset);
  const totalCount = data.hits?.total?.value || 0;
  const nextFrom = from + size;

  return {
    items,
    totalCount,
    nextPage: nextFrom < totalCount ? nextFrom : undefined,
  };
};

/**
 * 특정 타입의 최근 변경된 자산을 조회합니다
 * @param entityType - 자산 타입 (table, dashboard, pipeline 등)
 * @returns 해당 타입의 최근 변경된 자산 목록
 */
export const fetchMyAssetsByType = async (entityType: string): Promise<MyAsset[]> => {
  const params = createQueryParams({
    index: INDICES.DATA_ASSET,
    sort_field: 'updatedAt',
    sort_order: 'desc',
    exclude_source_fields: 'columns',
  });

  const typeFilter = createEntityTypeFilter(entityType);
  params.query_filter = JSON.stringify(typeFilter);

  return await fetchAssetsFromApi(params);
};
